/**
 * Shared numbers and copy for the card-based shareholder graph.
 *
 * One place for every size, zoom and timing value, so the prepare step, the ELK input, the React
 * Flow nodes and the PNG export can never disagree about how big a card is.
 */
import { PUNCTUATION } from '../../../../../utils/constants/notation';

// Card box. React Flow nodes get these as explicit width/height, so edges and the export bounds
// never depend on DOM measurement.
export const CARD_WIDTH = 248;
export const CARD_HEIGHT = 136;

// The synthetic "further shareholders" node. A fixed id keeps table keys and topologyKey stable.
export const AGGREGATE_ID = 'aggregate';
export const AGGREGATE_NAME = 'Aggregated filtered ultimate shareholders';

export const NODE_KIND = {
  TARGET: 'target',
  OWNER: 'owner',
  SUBSIDIARY: 'subsidiary',
  AGGREGATE: 'aggregate'
};

// Why owners are not shown: the share filter alone, the filters together (the share filter and
// 'Unknown values: excluded'), or no owner on record at all.
export const HIDDEN_OWNERS_CAUSE = Object.freeze({
  SHARE_FILTER: 'shareFilter',
  FILTERS: 'filters',
  NONE_ON_RECORD: 'noneOnRecord'
});

// Icon paths of the shareholder structure legend; the cards draw the same SVGs.
export const SHAREHOLDER_LEGEND_ICONS = {
  target: '/icons/clean/crosshair.svg',
  company: '/icons/clean/bank.svg',
  person: '/icons/clean/person.svg',
  entity: '/icons/clean/people.svg',
  share: '/icons/clean/pie-chart.svg',
  beneficial: '/icons/clean/percent.svg',
  linkShare: '/icons/shareholder-share-label-icon.svg',
  subsidiary: '/icons/clean/building.svg'
};

// Raw share value for a general partner holding no equity.
export const GENERAL_PARTNER_SHARE = 'GP';

// Share shown for an intermediate owner (neither an ultimate owner nor a direct shareholder): its
// share in the target is not relevant, so it gets a dash instead of a chip.
export const NOT_APPLICABLE_SHARE = PUNCTUATION.EN_DASH;

// What the backend's share_in_target is (share_in_target_basis on every owner node).
export const SHARE_IN_TARGET_BASIS = Object.freeze({
  BENEFICIAL: 'beneficial',
  DIRECT: 'direct',
  NOT_APPLICABLE: 'not_applicable'
});

// Joins several shares that sit on one merged edge, e.g. 'GP · 30%'.
export const SHARE_SEPARATOR = ' · ';

// Zoom. Below COMPACT_ZOOM the cards switch to the compact row. The initial view fits the whole
// graph; MIN_ZOOM is the zoom-out limit, low enough for a wide graph to fit a laptop-width canvas.
export const MIN_ZOOM = 0.1;
// The whole-graph fit may go below MIN_ZOOM for very wide graphs, so the target is always in view;
// the zoom-out limit then follows the fit.
export const FIT_MIN_ZOOM = 0.01;
export const MAX_ZOOM = 1.5;
export const COMPACT_ZOOM = 0.55;
export const INITIAL_FIT_MAX_ZOOM = 1;
export const INITIAL_FIT_PADDING = 0.08;

// Above this many nodes React Flow only renders what is in the viewport.
export const LARGE_GRAPH_NODES = 200;

// ELK layered spacing. edgeNodeBetweenLayers leaves room for the share chip above the first bend.
export const ELK_SPACING = {
  nodeNode: 48,
  betweenLayers: 88,
  edgeNodeBetweenLayers: 40
};

export const LAYOUT_TIMEOUT_MS = 15000;
// How long the layout worker (1.6 MB) may take to load before the fallback layout is used; the
// layout timeout starts once it answers.
export const WORKER_READY_TIMEOUT_MS = 30000;
export const LAYOUT_CACHE_SIZE = 8;
// A fallback layout (worker blocked, broken or too slow) is tried again on a later view of the
// same topology, up to this many layout runs in all.
export const MAX_LAYOUT_ATTEMPTS = 2;

// Share chip on an edge.
export const CHIP_HEIGHT = 20;

// PNG export caps. iOS and iPadOS Safari back no canvas over 16,777,216 px (4096 x 4096) and
// return an empty image instead, so the area cap is that limit on every device.
export const PNG_MAX_SIDE = 8000;
export const PNG_MAX_AREA = 16777216;
export const PNG_PIXEL_RATIO = 2;
export const PNG_PADDING = 0.05;

// Fixed height of the Ultimate shareholders and Subsidiaries tables below the full-width graph.
export const SHAREHOLDER_TABLE_HEIGHT = 360;

// Desktop views of the Shareholders tab: the graph with the two tables, or the levels table.
export const STRUCTURE_VIEWS = Object.freeze({ GRAPH: 'graph', TABLE: 'table' });

// The basic figure tooltip as in the search list (CompanyResult.jsx); the search list's 'This
// company was included…' only fits search results. The gross-profit one is GROSS_PROFIT_TOOLTIP
// (dataSources.js), shared with the General tab.
export const BASIC_FIGURE_TOOLTIP = 'Based on self-disclosure or estimate';

export const GRAPH_COPY = {
  EMPTY: 'No shareholder structure available for this company.',
  NO_OWNERS_AT_SHARE: (percentageFilter) => `No shareholders at or above ${percentageFilter}%.`,
  NO_OWNERS_FILTERED: 'No shareholders match the current filters.',
  NO_OWNERS_ON_RECORD: 'No shareholders on record.',
  CHANGE_FILTER: 'Change filter',
  CHUNK_ERROR: 'Could not load the shareholder graph. Reload the page to try again.',
  NODE_A11Y_DESCRIPTION: 'Press Enter to open details. Press Shift+F10 for more options.',
  SKIP_GRAPH: 'Skip graph',
  MOBILE_HINT:
    'Share = share in the target, incl. indirect holdings for ultimate owners (blue) and the direct stake for direct shareholders. Blue figure = self-disclosed.',
  SHOW_ALL: 'Show all',
  SHOW_ALL_LABEL: (label) => `Show all: ${label}`,
  AGGREGATE_BELOW_SHARE: (percentageFilter) => `Below the ${percentageFilter}% filter`,
  AGGREGATE_BELOW_FILTER: 'Below the filter',
  AGGREGATE_FILTERED: 'Hidden by the current filters',
  SUBSIDIARIES: 'Subsidiaries',
  OPEN_GRAPH: 'Open graph',
  OPEN_IN_NEW_TAB: 'Open in new tab',
  VIEW_SWITCH: 'Shareholder structure view',
  VIEW_GRAPH: 'Graph',
  VIEW_TABLE: 'Table',
  HIDDEN_ULTIMATES: (count) =>
    `${count} ultimate owner${count === 1 ? ' is' : 's are'} hidden behind collapsed owners.`,
  SHOW_ALL_OWNERS: 'Show all owners',
  SHOW_OWNERS: 'Show owners',
  HIDE_OWNERS: 'Hide owners',
  // A table row whose card the graph does not draw
  SHOW_IN_GRAPH: 'Show in graph',
  SHOW_OWNER_IN_GRAPH_HINT: 'Show in graph (shows the owners of all collapsed cards)',
  SHOW_SUBSIDIARY_IN_GRAPH_HINT: 'Show in graph (includes subsidiaries)'
};

// The page's answer once the PNG exists (onConfirmDownload): the download meter recorded it, the
// allowance is used up, or the meter could not be reached. Only a recorded download is saved.
export const DOWNLOAD_VERDICT = Object.freeze({
  RECORDED: 'recorded',
  LIMIT: 'limit',
  FAILED: 'failed'
});
